import React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  Button,
  Grid,
  IconButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Authenticated from "../components/Layouts/Authenticated";
import {
  UserPlaceholderIcon,
  SupportIcon,
  RobotIcon,
  ConnectIcon,
  WebFormIcon,
  ComputerAnalysisIcon,
} from "../components/ui";
import { createDashboardStyles } from "./styles";
import { ROUTES } from "../constants/routes";
import { useAuthContext } from "../contexts/AuthContext";
import { getStaticAssetPath } from "../utils/staticAssets";
import { User } from "../types/api";
import { useFeaturedRecipes } from "../hooks/useFeaturedRecipes";
import Carousel from "../components/Carousel/Carousel";
import FeedbackIcon from "../components/ui/Feedback";
import { useIsEnterprise } from "../hooks/useIsEnterprise";

/**
 * Dashboard Page Component
 *
 * Replicates the layout from MVP Dashboard.jsx with Material-UI components.
 * Features user information, navigation cards, and placeholder content areas.
 */
const Dashboard: React.FC = () => {
  // Styles
  const theme = useTheme();
  const dashboardStyles = createDashboardStyles(theme);

  // Hooks
  const { user } = useAuthContext();
  const { recipes: featuredRecipes, loading } = useFeaturedRecipes();

  // Enterprise access: hostname check + PostHog cohort flag
  const { isEnterprise } = useIsEnterprise();

  // Get user avatar
  const getUserAvatar = (user: User | null) => {
    if (user) {
      return getStaticAssetPath(user.image || "");
    }
    return "";
  };

  // Get user name
  const getUserName = (user: User | null) => {
    if (user) {
      return user.name || "";
    }
    return "";
  };

  // Get user first letter of name
  const getUserFirstLetter = (user: User | null) => {
    if (user) {
      if (user.name) {
        return user.name.charAt(0).toUpperCase();
      }
      return "";
    }
    return "";
  };

  return (
    <Authenticated>
      <title>Dashboard - Materiom Commons</title>
      <Container maxWidth="xl" sx={dashboardStyles.container}>
        {/* Introduction Bar at top of Dashboard */}
        <Box sx={dashboardStyles.topSection}>
          <Card sx={dashboardStyles.introCard}>
            <Box sx={dashboardStyles.userInfoContainer}>
              {/* User Avatar */}
              <Box sx={dashboardStyles.userAvatarContainer}>
                <Avatar
                  src={getUserAvatar(user)}
                  alt={getUserName(user)}
                  sx={dashboardStyles.userAvatarContainer}
                >
                  {/* Fallback: first letter of name */}
                  {getUserFirstLetter(user)}
                </Avatar>
              </Box>
              {/* User Text */}
              <Box sx={dashboardStyles.userTextContainer}>
                <Typography variant="body2" sx={dashboardStyles.dashboardLabel}>
                  Dashboard
                </Typography>
                <Typography variant="h4" sx={dashboardStyles.welcomeMessage}>
                  Welcome {getUserName(user)}!
                </Typography>
              </Box>
              {/* Feedback Icon */}
              <Tooltip title="Send feedback about your experience" arrow>
                <IconButton
                  href="https://forms.monday.com/forms/f2803439577a06803a008d9856a8e1fc?r=use1"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={dashboardStyles.feedbackIconButton}
                  disableRipple
                >
                  <FeedbackIcon sx={dashboardStyles.feedbackIcon} />
                </IconButton>
              </Tooltip>
              {/* Support Icon */}
              <Tooltip title="Contact support for help" arrow>
                <IconButton
                  href="mailto:support@materiom.org"
                  sx={dashboardStyles.supportIconButton}
                  disableRipple
                >
                  <SupportIcon sx={dashboardStyles.supportIcon} />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        </Box>

        {/* Dashboard Content Grid */}
        <Box sx={dashboardStyles.contentGrid}>
          {/* Unified Grid - Navigation Cards + Featured Content */}
          <Grid container sx={dashboardStyles.unifiedGrid}>
            {/* Materiom AI */}
            <Card
              component="a"
              href={ROUTES.MATERIOM_AI}
              sx={{
                ...dashboardStyles.navigationCard,
                ...dashboardStyles.materiomAICard,
              }}
            >
              <RobotIcon
                sx={{
                  ...dashboardStyles.cardIcon,
                  ...dashboardStyles.robotIcon,
                }}
              />
              <Typography variant="body2" sx={dashboardStyles.cardSubtitle}>
                Discover
              </Typography>
              <Typography variant="h6" sx={dashboardStyles.cardTitle}>
                {isEnterprise ? "Materiom AI" : "Materiom AI Chatbot (beta)"}
              </Typography>
              <Button variant="contained" sx={dashboardStyles.ctaButton}>
                {isEnterprise ? "Go" : "Try it now"}
              </Button>
            </Card>

            {/* Materials Database */}
            <Card
              component="a"
              href={ROUTES.MATERIALS_DATABASE}
              sx={{
                ...dashboardStyles.navigationCard,
                ...dashboardStyles.standardCardHover,
              }}
            >
              <ComputerAnalysisIcon
                sx={{
                  ...dashboardStyles.cardIcon,
                  ...dashboardStyles.computerAnalysisIcon,
                }}
              />
              <Typography variant="body2" sx={dashboardStyles.cardSubtitle}>
                Search & Analyse
              </Typography>
              <Typography variant="h6" sx={dashboardStyles.cardTitle}>
                Materials Database
              </Typography>
              <Button variant="contained" sx={dashboardStyles.ctaButton}>
                Go
              </Button>
            </Card>

            {/* User Directory - hidden for enterprise users */}
            {!isEnterprise && (
              <Card
                component="a"
                href={ROUTES.USER_DIRECTORY}
                sx={{
                  ...dashboardStyles.navigationCard,
                  ...dashboardStyles.standardCardHover,
                  ...dashboardStyles.userDirectoryCard,
                }}
              >
                <ConnectIcon
                  sx={{
                    ...dashboardStyles.cardIcon,
                    ...dashboardStyles.standardIcon,
                  }}
                />
                <Typography variant="body2" sx={dashboardStyles.cardSubtitle}>
                  Connect
                </Typography>
                <Typography variant="h6" sx={dashboardStyles.cardTitle}>
                  User Directory
                </Typography>
                <Button variant="contained" sx={dashboardStyles.ctaButton}>
                  Go
                </Button>
              </Card>
            )}

            {/* Contribution Portal - hidden for enterprise users */}
            {!isEnterprise && (
              <Card
                component="a"
                href={ROUTES.CONTRIBUTION_PORTAL}
                sx={{
                  ...dashboardStyles.navigationCard,
                  ...dashboardStyles.standardCardHover,
                  ...dashboardStyles.contributionCard,
                }}
              >
                <WebFormIcon
                  sx={{
                    ...dashboardStyles.cardIcon,
                    ...dashboardStyles.standardIcon,
                  }}
                />
                <Typography variant="body2" sx={dashboardStyles.cardSubtitle}>
                  Contribute
                </Typography>
                <Typography variant="h6" sx={dashboardStyles.cardTitle}>
                  Contribution Portal
                </Typography>
                <Button variant="contained" sx={dashboardStyles.ctaButton}>
                  Go
                </Button>
              </Card>
            )}

            {/* Featured Recipes Carousel */}
            <Card sx={dashboardStyles.featuredRecipesCard}>
              <Carousel recipes={featuredRecipes} />
            </Card>
          </Grid>
        </Box>
      </Container>
    </Authenticated>
  );
};

export default Dashboard;
